/**
 * Apollo Link for OpenTelemetry W3C Trace Context Propagation
 *
 * This module provides an Apollo Link that automatically injects W3C trace
 * context headers into GraphQL requests and captures them from responses.
 */

import { ApolloLink } from '@apollo/client'
import { getTraceContextHeaders, updateTraceContext } from './traceContext'

/**
 * Create an Apollo Link that handles W3C trace context propagation.
 *
 * This link:
 * 1. Injects traceparent/tracestate headers into outgoing GraphQL requests
 * 2. Captures traceparent/tracestate headers from responses
 * 3. Maintains trace continuity across client-server boundaries
 *
 * Usage:
 * ```typescript
 * import { createTraceContextLink } from '@/gf/utils/apolloTraceLink'
 *
 * const client = new ApolloClient({
 *   link: ApolloLink.from([
 *     createTraceContextLink(),
 *     // ... other links
 *     httpLink,
 *   ]),
 * })
 * ```
 */
export function createTraceContextLink(): ApolloLink {
  return new ApolloLink((operation, forward) => {
    // Inject trace context headers into the request
    const traceHeaders = getTraceContextHeaders()

    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        ...traceHeaders,
      },
    }))

    // Forward the operation and capture response headers
    return forward(operation).map((response) => {
      // Extract trace context from response headers if available
      const context = operation.getContext()
      const responseHeaders = context.response?.headers

      if (responseHeaders) {
        updateTraceContext(responseHeaders)
      }

      return response
    })
  })
}
